import {
  Alert,
  Button,
  Checkbox,
  CloseButton,
  Group,
  Modal,
  Popover,
  SegmentedControl,
  Skeleton,
  Stack,
  Text,
  Textarea,
  UnstyledButton,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArrowBackUp,
  IconCalendarOff,
  IconCircleCheckFilled,
  IconClock,
  IconClockHour4,
  IconDownload,
  IconPrinter,
  IconSignature,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReauthField, useReauth } from '../../approval-policy.js';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime, formatDay } from '../../format.js';
import { StatusBadge } from '../../StatusBadge.js';
import { PeriodNav } from './PeriodNav.js';
import { browserPdfOutput, pdfErrorKey, renderPdf } from './pdf.js';
import classes from './RestMonthView.module.css';
import { formatHours } from './rest-hours.js';
import {
  clockNoteText,
  loadRestMonth,
  monthStatusTone,
  monthTable,
  shiftMonth,
} from './rest-month.js';
import {
  type PdfLanguages,
  pdfLanguage,
  restMonthPdf,
  restMonthPdfFileName,
} from './rest-month-pdf.js';
import { colorOf, DAY_ICON, DAY_TONE, RestNotice, restTable } from './rest-ui.js';
import { offsetAt, type ShipTime } from './ship-clock.js';
import { errorCode, type PersonnelSource } from './source.js';
import type { CrewRestDay, RestMonth, RestMonthStatusName, WorkPeriod } from './types.js';

export interface RestMonthViewProps {
  source: PersonnelSource;
  vesselId: string;
  /** Gemi saati: dilim ya da saat kaydının çizelgesi (ADR 0065 §4). */
  timeZone: ShipTime;
  /** PDF kopyasının başlığı için (ADR 0024 #12). */
  vessel: { name: string; imo: string | null; workingLanguage?: string | null | undefined };
  person: { userId: string; fullName: string; rank: string | null };
  /** Gösterilen ay (`YYYY-MM`); ekran tutar, kişi değişince korunur (M6f G3-23). */
  month: string;
  onMonthChange: (month: string) => void;
  /** Gemi yerel bugünü: gelecek ay seçilmez; bugünden sonraki günler "yaklaşan". */
  today: string;
  nameOf: (userId: string) => string;
}

type Loaded =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; record: RestMonth; days: CrewRestDay[]; periods: WorkPeriod[] };

/** Kayıt durumunun ikonu (G3-22): imza beklemede saat, iade geri ok, onay ve imza. */
const RECORD_ICON: Readonly<Record<RestMonthStatusName, ReactNode>> = {
  draft: <IconSignature size={13} stroke={2} />,
  pending: <IconClock size={13} stroke={2.2} />,
  returned: <IconArrowBackUp size={13} stroke={2.2} />,
  approved: <IconCircleCheckFilled size={13} />,
  reapproval_pending: <IconClock size={13} stroke={2.2} />,
};

/** Saat dilimi farkı etiketi ("UTC+3", "UTC−4:30"; eksi U+2212, plan §0.6). */
function zoneLabel(offsetMin: number): string {
  const sign = offsetMin < 0 ? '−' : '+';
  const abs = Math.abs(offsetMin);
  const minutes = abs % 60;
  return `UTC${sign}${Math.floor(abs / 60)}${minutes ? `:${String(minutes).padStart(2, '0')}` : ''}`;
}

const pad = (value: number) => String(value).padStart(2, '0');
const clock = (minutes: number) => `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;

/**
 * Aylık kayıt (BGM "Monthly Calendar"; ILO/IMO aylık kayıt biçimi; ADR 0021): ayın her günü,
 * günün çalışma aralıkları, dinlenme değerleri, istisnalar ve imzalar. Gemide gemi adamı ayını
 * imzalar, kaptan onaylar ya da iade eder; ikisinde de parola yeniden istenir [MLC A2.3 §12].
 */
export function RestMonthView(props: RestMonthViewProps) {
  const { source, vesselId, timeZone, vessel, person, month, onMonthChange, today, nameOf } = props;
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const [loaded, setLoaded] = useState<Loaded>({ kind: 'loading' });
  const [dialog, setDialog] = useState<'sign' | 'decide' | null>(null);
  const [pdf, setPdf] = useState<{ busy: 'save' | 'print' | null; error: string | null }>({
    busy: null,
    error: null,
  });

  // Ay hızlı değişince önceki ayın geç gelen yanıtı yeni ayın üstüne yazılmasın: eski ayın
  // günleri yeni ayın tablosunda eşleşmez ve "kayıt yok" görünürdü (yarış; Windows e2e)
  const lastRequest = useRef(0);
  const load = useCallback(() => {
    const request = ++lastRequest.current;
    setLoaded({ kind: 'loading' });
    // Ayın aralığı; seçili günün liste üyeliğine bakmaz: ayrılan kişinin ayı da açılır (P1)
    loadRestMonth(source, { vesselId, userId: person.userId, month, today })
      .then((data) => {
        if (request !== lastRequest.current) return;
        setLoaded({ kind: 'ready', ...data });
      })
      .catch((error: unknown) => {
        if (request !== lastRequest.current) return;
        setLoaded({ kind: 'error', code: errorCode(error) });
      });
  }, [source, vesselId, person.userId, month, today]);
  useEffect(load, [load]);

  const rows = useMemo(
    () =>
      loaded.kind === 'ready'
        ? monthTable(loaded.days, loaded.periods, timeZone, month, today)
        : [],
    [loaded, timeZone, month, today],
  );
  const monthLabel = new Intl.DateTimeFormat(locale === 'en' ? 'en-US' : locale, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${month}-01T00:00:00Z`));
  const weekday = useMemo(
    () =>
      new Intl.DateTimeFormat(locale === 'en' ? 'en-GB' : locale, {
        weekday: 'short',
        timeZone: 'UTC',
      }),
    [locale],
  );
  const record = loaded.kind === 'ready' ? loaded.record : null;
  const editor = source.editor;
  const recorded = rows.some((row) => row.status !== null);
  // Ayda görülen iş türleri: araç çubuğunda küçük lejant (G3-39)
  const activities = useMemo(
    () => [...new Set(rows.flatMap((row) => row.work.map((w) => w.activity ?? 'other')))],
    [rows],
  );
  const atShipTime = (at: string) => {
    const offset = offsetAt(timeZone, Date.parse(at));
    return `${formatDateTime(at, offset)} (${zoneLabel(offset)})`;
  };
  const timeline = record
    ? [
        ...record.submissions.map((s) => ({
          at: s.at,
          text: t('restHours.month.signed', { name: nameOf(s.by), revision: s.revision }),
          hash: s.contentHash,
          reason: null as string | null,
          kind: 'signed',
        })),
        ...record.approvals.map((a) => ({
          at: a.at,
          text: t(`restHours.month.${a.decision}`, { name: nameOf(a.by) }),
          hash: a.contentHash,
          reason: a.reason,
          kind: a.decision,
        })),
      ].sort((x, y) => x.at.localeCompare(y.at))
    : [];
  const latest = timeline.at(-1) ?? null;

  /**
   * Aylık kaydın PDF kopyası (ADR 0024 #12): ekrandaki verinin aynısından, gemide ve ofiste aynı
   * fonksiyonla. Her zaman İngilizce; geminin çalışma dili başkaysa iki dilli [MLC A2.3 §10].
   */
  const exportPdf = (kind: 'save' | 'print') => {
    if (!record) return;
    const second = vessel.workingLanguage ?? null;
    const languages: PdfLanguages = {
      primary: pdfLanguage(i18n.getFixedT('en'), 'en'),
      secondary:
        second && second !== 'en' && i18n.hasResourceBundle(second, 'translation')
          ? pdfLanguage(i18n.getFixedT(second), second)
          : null,
    };
    const names: Record<string, string> = {};
    for (const entry of [...record.submissions, ...record.approvals]) {
      names[entry.by] = nameOf(entry.by);
    }
    const definition = restMonthPdf(
      {
        vessel: { name: vessel.name, imo: vessel.imo },
        seafarer: { fullName: person.fullName, rank: person.rank },
        month,
        timeZone,
        rows,
        record,
        ...(record.clockNotes ? { clockNotes: record.clockNotes } : {}),
        names,
        generatedAt: new Date().toISOString(),
      },
      languages,
    );
    const fileName = restMonthPdfFileName({ month, seafarer: person }, languages.primary.t);
    const output = source.pdf ?? browserPdfOutput();
    const render = () => renderPdf(definition, { print: kind === 'print' });
    setPdf({ busy: kind, error: null });
    (kind === 'save' ? output.save(fileName, render) : output.print(fileName, render))
      .then(() => setPdf({ busy: null, error: null }))
      .catch((error: unknown) => setPdf({ busy: null, error: errorCode(error) }));
  };

  return (
    <div className={classes.root}>
      <div className={classes.toolbar}>
        <PeriodNav
          label={monthLabel}
          previousLabel={t('restHours.month.previous')}
          nextLabel={t('restHours.month.next')}
          onPrevious={() => onMonthChange(shiftMonth(month, -1))}
          onNext={() => onMonthChange(shiftMonth(month, 1))}
          nextDisabled={shiftMonth(month, 1) > today.slice(0, 7)}
        />
        {record ? (
          <StatusBadge
            tone={monthStatusTone(record.state.status)}
            icon={RECORD_ICON[record.state.status]}
            outlined
          >
            <span className={classes.recordPrefix}>{t('restHours.month.record')}</span>
            <span>{t(`restHours.month.status.${record.state.status}`)}</span>
          </StatusBadge>
        ) : loaded.kind === 'loading' ? (
          <Skeleton height={22} width={150} radius="xl" />
        ) : null}
        <span className={classes.spacer} />
        <Button
          size="sm"
          variant="default"
          leftSection={<IconDownload size={16} />}
          loading={pdf.busy === 'save'}
          disabled={!record || pdf.busy !== null}
          onClick={() => exportPdf('save')}
        >
          {t('restHours.pdf.download')}
        </Button>
        <Button
          size="sm"
          variant="default"
          leftSection={<IconPrinter size={16} />}
          loading={pdf.busy === 'print'}
          disabled={!record || pdf.busy !== null}
          onClick={() => exportPdf('print')}
        >
          {t('restHours.pdf.print')}
        </Button>
        {editor && record?.can?.sign ? (
          <Button
            size="sm"
            leftSection={<IconSignature size={16} />}
            onClick={() => setDialog('sign')}
          >
            {t('restHours.month.sign')}
          </Button>
        ) : null}
        {editor && record?.can?.decide ? (
          <Button
            size="sm"
            leftSection={<IconSignature size={16} />}
            onClick={() => setDialog('decide')}
          >
            {t('restHours.month.review')}
          </Button>
        ) : null}
      </div>
      {/* İmza özeti ve lejant: araç çubuğunun altında tek satır (G3-39, G3-41) */}
      <div className={classes.meta}>
        {latest ? (
          <Popover width={360} position="bottom-start" withinPortal shadow="md">
            <Popover.Target>
              <UnstyledButton className={classes.summary}>
                <span>{latest.text}</span>
                <span className={classes.summaryTime}> · {atShipTime(latest.at)}</span>
              </UnstyledButton>
            </Popover.Target>
            <Popover.Dropdown>
              <p className={classes.popoverTitle}>{t('restHours.month.signatures')}</p>
              <ol className={classes.trail}>
                {timeline.map((entry) => (
                  <li key={`${entry.at}-${entry.text}`}>
                    <span className={classes.trailDot} data-kind={entry.kind} aria-hidden />
                    <span className={classes.trailBody}>
                      <span className={classes.trailTime}>{atShipTime(entry.at)}</span>
                      <span>{entry.text}</span>
                      <span className={classes.trailHash}>
                        {t('restHours.month.hash', { hash: entry.hash.slice(0, 8) })}
                      </span>
                      {entry.reason ? <span className={classes.reason}>{entry.reason}</span> : null}
                    </span>
                  </li>
                ))}
              </ol>
            </Popover.Dropdown>
          </Popover>
        ) : record ? (
          <span className={classes.summaryMuted}>{t('restHours.month.notSigned')}</span>
        ) : null}
        <span className={classes.spacer} />
        {activities.length > 0 ? (
          <span className={classes.legend} aria-hidden>
            {activities.slice(0, 4).map((activity) => (
              <span key={activity}>
                <i style={{ background: colorOf(activity) }} />
                {t(`restHours.activity.${activity}`, { defaultValue: activity })}
              </span>
            ))}
          </span>
        ) : null}
      </div>

      {pdf.error ? (
        <RestNotice
          tone="danger"
          role="alert"
          icon={<IconAlertCircle size={18} />}
          action={
            <CloseButton
              size="sm"
              aria-label={t('restHours.edit.close')}
              onClick={() => setPdf({ busy: null, error: null })}
            />
          }
        >
          {t(pdfErrorKey(pdf.error))}
        </RestNotice>
      ) : null}
      {loaded.kind === 'error' ? (
        <RestNotice
          tone="danger"
          role="alert"
          icon={<IconAlertCircle size={18} />}
          action={
            <Button size="xs" variant="light" color="red" onClick={load}>
              {t('common.retry')}
            </Button>
          }
        >
          {t('restHours.month.error')}
        </RestNotice>
      ) : null}
      {record?.clockNotes && record.clockNotes.length > 0 ? (
        // Gemi saati notları (ADR 0065 §7): kayıtta ve PDF'te aynı metin
        <RestNotice tone="info" icon={<IconClockHour4 size={18} />}>
          <p className={classes.noticeTitle}>{t('restHours.clock.notes')}</p>
          <ul className={classes.list}>
            {record.clockNotes.map((note) => {
              const text = clockNoteText(note, (key, options) => t(key, options ?? {}), formatDay);
              return <li key={text}>{text}</li>;
            })}
          </ul>
        </RestNotice>
      ) : null}
      {record && record.exceptions.length > 0 ? (
        <RestNotice tone="warning" icon={<IconAlertTriangle size={18} />}>
          <p className={classes.noticeTitle}>
            {t('restHours.month.exceptions', { count: record.exceptions.length })}
          </p>
          <ul className={classes.list}>
            {record.exceptions.map((e) => (
              <li key={`${e.date}-${e.rule}`}>
                {formatDay(e.date)} ·{' '}
                {t(`restHours.rule.${e.rule}`, {
                  value: Math.round(e.value * 100) / 100,
                  limit: e.limit,
                })}
              </li>
            ))}
          </ul>
        </RestNotice>
      ) : null}

      {loaded.kind === 'error' ? null : record && !recorded ? (
        <EmptyState
          icon={<IconCalendarOff size={22} stroke={1.8} />}
          title={t('restHours.month.empty', { month: monthLabel })}
          description={t('restHours.month.emptyText')}
        />
      ) : (
        <section
          className={`${restTable.frame} ${classes.tableWrap}`}
          aria-label={t('restHours.month.table', { month: monthLabel })}
          aria-busy={record ? undefined : true}
          // Aylık çizelge bilerek yana kayar (otuz bir gün sığmaz; e2e tablo kayma denetimi)
          data-allow-scroll
          // Yatay ve dikey kayan tablo klavyeyle de kaydırılabilsin (WCAG 2.1.1)
          // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge
          tabIndex={0}
        >
          <table className={`${restTable.table} ${classes.table}`}>
            <thead>
              <tr>
                <th scope="col">{t('restHours.columns.date')}</th>
                <th scope="col" className={classes.bandHead}>
                  <span className={classes.hours} aria-hidden>
                    {[0, 6, 12, 18, 24].map((hour) => (
                      <span
                        key={hour}
                        data-edge={hour === 0 ? 'start' : hour === 24 ? 'end' : undefined}
                        style={{ left: `${(hour / 24) * 100}%` }}
                      >
                        {pad(hour)}
                      </span>
                    ))}
                  </span>
                  <span className={classes.visuallyHidden}>{t('restHours.month.band')}</span>
                </th>
                <th scope="col">{t('restHours.columns.work')}</th>
                <th scope="col">{t('restHours.columns.rest')}</th>
                <th scope="col">{t('restHours.columns.rest24h')}</th>
                <th scope="col">{t('restHours.columns.rest7d')}</th>
                <th scope="col">{t('restHours.columns.status')}</th>
              </tr>
            </thead>
            <tbody>
              {record
                ? rows.map((row) => {
                    const date = new Date(`${row.date}T00:00:00Z`);
                    const weekend = date.getUTCDay() === 0 || date.getUTCDay() === 6;
                    const value = (hours: number | null) =>
                      row.upcoming ? '' : hours === null ? '—' : formatHours(hours);
                    return (
                      <tr
                        key={`${row.date}#${row.occurrence ?? 1}`}
                        data-skipped={row.skippedDay || undefined}
                        data-weekend={weekend || undefined}
                        data-upcoming={row.upcoming || undefined}
                        aria-current={row.date === today ? 'date' : undefined}
                      >
                        <td className={classes.date}>
                          <span className={restTable.num}>{row.date.slice(8, 10)}</span>{' '}
                          <span className={classes.weekday}>{weekday.format(date)}</span>
                          {row.occurrence ? (
                            <span className={classes.weekday}>
                              {' '}
                              {t(`restHours.clock.occurrence${row.occurrence}`)}
                            </span>
                          ) : null}
                          {row.minutes !== undefined &&
                          row.minutes !== 1440 &&
                          !row.skippedDay &&
                          !row.occurrence ? (
                            // Saat değişen günün süresi (ADR 0065 §7)
                            <span className={classes.dayLength}>
                              {t('restHours.clock.dayLength', {
                                hours:
                                  row.minutes % 60 === 0
                                    ? row.minutes / 60
                                    : `${Math.floor(row.minutes / 60)}:${pad(row.minutes % 60)}`,
                              })}
                            </span>
                          ) : null}
                        </td>
                        <td>
                          <div
                            className={classes.band}
                            role="img"
                            aria-label={
                              row.skippedDay
                                ? t('restHours.clock.dateSkipped')
                                : row.upcoming
                                  ? t('restHours.month.upcoming')
                                  : row.work.length === 0
                                    ? t('restHours.month.noWork')
                                    : row.work
                                        .map((w) => `${clock(w.from)}–${clock(w.to)}`)
                                        .join(', ')
                            }
                          >
                            {/* Yaşanmayan duvar saati taralı (ADR 0065 §7) */}
                            {(row.skippedDay ? [{ from: 0, to: 1440 }] : (row.notLived ?? [])).map(
                              (g) => (
                                <span
                                  key={`gap-${g.from}`}
                                  className={classes.notLived}
                                  style={{
                                    left: `${(g.from / 1440) * 100}%`,
                                    width: `${((g.to - g.from) / 1440) * 100}%`,
                                  }}
                                />
                              ),
                            )}
                            {row.work.map((w) => (
                              <span
                                key={w.from}
                                className={classes.work}
                                style={{
                                  left: `${(w.from / 1440) * 100}%`,
                                  width: `${((w.to - w.from) / 1440) * 100}%`,
                                  background: colorOf(w.activity),
                                }}
                              />
                            ))}
                          </div>
                        </td>
                        <td className={restTable.num}>{value(row.workHours)}</td>
                        <td className={restTable.num}>{value(row.restHours)}</td>
                        <td className={restTable.num}>{value(row.rest24h)}</td>
                        <td className={restTable.num}>{value(row.rest7d)}</td>
                        <td>
                          {row.upcoming ? (
                            <span className={classes.upcoming}>
                              {t('restHours.month.upcoming')}
                            </span>
                          ) : row.status ? (
                            <StatusBadge
                              tone={DAY_TONE[row.status]}
                              icon={(() => {
                                const Icon = DAY_ICON[row.status];
                                return <Icon size={12} stroke={2.2} />;
                              })()}
                            >
                              {t(`restHours.status.${row.status}`)}
                              {row.exception ? ` · ${t('restHours.month.exception')}` : ''}
                            </StatusBadge>
                          ) : (
                            <span className={classes.muted}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                : Array.from({ length: 8 }, (_, n) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: sabit iskelet satırları
                    <tr key={n} aria-hidden>
                      <td>
                        <Skeleton height={10} width={44} />
                      </td>
                      <td>
                        <Skeleton height={12} />
                      </td>
                      {[0, 1, 2, 3, 4].map((m) => (
                        <td key={m}>
                          <Skeleton height={10} width={38} />
                        </td>
                      ))}
                    </tr>
                  ))}
            </tbody>
          </table>
        </section>
      )}

      {editor && record && dialog ? (
        <MonthDialog
          kind={dialog}
          record={record}
          fullName={person.fullName}
          monthLabel={monthLabel}
          onClose={() => setDialog(null)}
          onSubmit={async (input) => {
            if (dialog === 'sign') {
              await editor.signRestMonth(month, input.password, record.contentHash);
            } else {
              await editor.decideRestMonth(month, {
                userId: person.userId,
                ...(input.password !== undefined ? { password: input.password } : {}),
                contentHash: record.contentHash,
                decision: input.decision,
                ...(input.reason ? { reason: input.reason } : {}),
                exceptionsAcknowledged: input.acknowledged,
              });
            }
            setDialog(null);
            load();
          }}
        />
      ) : null}
    </div>
  );
}

interface DialogInput {
  /** Yalnız girildiyse (ADR 0075): parolasız onayda alan hiç gönderilmez. */
  password?: string;
  decision: 'approved' | 'returned';
  reason: string;
  acknowledged: boolean;
}

/**
 * İmza ve karar penceresi: açık onay adımı (düğme), iadede gerekçe, istisna onayı. Parola alanı
 * yalnız şirket ayarı açıkken ya da sunucu isterse görünür (ADR 0075).
 */
function MonthDialog(props: {
  kind: 'sign' | 'decide';
  record: RestMonth;
  fullName: string;
  monthLabel: string;
  onClose: () => void;
  onSubmit: (input: DialogInput) => Promise<void>;
}) {
  const { kind, record, fullName, monthLabel, onClose, onSubmit } = props;
  const { t } = useTranslation();
  const [input, setInput] = useState<Omit<DialogInput, 'password'>>({
    decision: 'approved',
    reason: '',
    acknowledged: false,
  });
  const reauth = useReauth('rest_hours_month');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const needsAck =
    kind === 'decide' && input.decision === 'approved' && record.exceptions.length > 0;
  const ready =
    !reauth.missing &&
    (input.decision !== 'returned' || kind === 'sign' || input.reason.trim() !== '') &&
    (!needsAck || input.acknowledged);

  return (
    <Modal
      opened
      onClose={onClose}
      title={t(kind === 'sign' ? 'restHours.month.signTitle' : 'restHours.month.reviewTitle', {
        name: fullName,
        month: monthLabel,
      })}
      closeButtonProps={{ 'aria-label': t('restHours.edit.close') }}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!ready) return;
          setBusy(true);
          setError(null);
          onSubmit({ ...input, ...reauth.body() })
            .catch((failure: unknown) => {
              reauth.noteError(failure);
              setError(errorCode(failure));
            })
            .finally(() => setBusy(false));
        }}
      >
        <Stack gap="md">
          <Text fz="sm">
            {t(kind === 'sign' ? 'restHours.month.signText' : 'restHours.month.reviewText')}
          </Text>
          {error ? (
            <Alert color="red" icon={<IconAlertCircle size={18} />} role="alert">
              {t(`restHours.month.errors.${error}`, {
                defaultValue: t('restHours.edit.errors.GENERIC'),
              })}
            </Alert>
          ) : null}
          {kind === 'decide' ? (
            <SegmentedControl
              aria-label={t('restHours.month.decision')}
              value={input.decision}
              onChange={(value) =>
                setInput({ ...input, decision: value as DialogInput['decision'] })
              }
              data={[
                { value: 'approved', label: t('restHours.month.approve') },
                { value: 'returned', label: t('restHours.month.return') },
              ]}
            />
          ) : null}
          {kind === 'decide' && input.decision === 'returned' ? (
            <Textarea
              label={t('restHours.month.reason')}
              required
              value={input.reason}
              onChange={(event) => setInput({ ...input, reason: event.currentTarget.value })}
              maxLength={1000}
            />
          ) : null}
          {needsAck ? (
            <Checkbox
              label={t('restHours.month.acknowledge', { count: record.exceptions.length })}
              checked={input.acknowledged}
              onChange={(event) =>
                setInput({ ...input, acknowledged: event.currentTarget.checked })
              }
            />
          ) : null}
          <ReauthField reauth={reauth} label={t('restHours.month.password')} mb="0" />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              {t('restHours.edit.cancel')}
            </Button>
            <Button type="submit" loading={busy} disabled={!ready}>
              {t(kind === 'sign' ? 'restHours.month.sign' : 'restHours.month.confirm')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
